import isEmpty from 'lodash/isEmpty';
import React from 'react';
import Select, { components, ValueType } from 'react-select';

import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';

import { Icon, Flag } from '@hydra/common/components';

import enrichCountryLanguage from '@hydra/utils/enrichCountryLanguage';
import tldToCountryCode from '@hydra/utils/tldToCountryCode';

import { useI18n } from '@hydra/common/customHooks';
import { SerpCountry, SerpSearchEngine, SerpSearchEngineLabel } from '@hydra/types/serp';

interface SerpSearchEngineLabelExtended extends SerpSearchEngineLabel {
  label: string
}

const topSearchEngines = [
  'au_en', 'br_pt', 'ca_en', 'ca_fr',
  'de_de', 'es_es', 'fr_fr', 'in_en',
  'mx_es', 'nl_nl', 'uk_en', 'us_en'
];

function mapToCountryLanguageList (countries: { [key: string]: SerpCountry }): string[] {
  return Object.keys(countries).reduce((countryCodes: string[], countryCode: string) => {
    const { supportedLang } = countries[countryCode];
    const list = supportedLang.map(lang => `${countryCode}_${lang}`);
    return countryCodes.concat(list);
  }, []);
}

const useStyles = makeStyles(theme => ({
  flag: {
    marginRight: theme.spacing(2)
  },
  title: {
    fontSize: theme.typography.fontSizeLarge,
    fontWeight: theme.typography.fontWeightSemibold,
    marginBottom: theme.spacing(0.75)
  },
  dropdownOptionsText: {
    fontSize: theme.typography.fontSizeMedium
  },
  selectedOptionText: {
    fontSize: theme.typography.fontSizeMedium,
    fontWeight: theme.typography.fontWeightSemibold
  }
}));

const sortSearchEngines = (searchEngines: SerpSearchEngineLabelExtended[]) => searchEngines.sort((a, b) => a.label.localeCompare(b.label));

const getLabel = (searchEngine: SerpSearchEngineLabel, translate: (value: string) => string) => {
  const { countryKey, languageKey } = searchEngine;
  const translationLangKey = languageKey.replace(/-/g, '_');
  return {
    ...searchEngine,
    label: languageKey
      ? `${translate(countryKey)} (${translate(translationLangKey)})`
      : translate(countryKey)
  };
};

const getGroupedCountries = ({ searchEngines, otherSearchEngines, translate }: {
  searchEngines: {
    [key: string]: SerpSearchEngineLabel;
  }
  otherSearchEngines: string[]
  translate: (value: string) => string
}) => {
  if (isEmpty(searchEngines) || isEmpty(otherSearchEngines)) {
    return [];
  }

  return [
    {
      label: translate('pdf_editor_advanced_ranked_keywords_top_search_engine_subtitle'),
      options: sortSearchEngines(topSearchEngines.map(seCode => getLabel(searchEngines[seCode], translate)))
    },
    {
      label: translate('pdf_editor_advanced_ranked_keywords_other_search_engine_subtitle'),
      options: sortSearchEngines(otherSearchEngines.map(seCode => getLabel(searchEngines[seCode], translate)))
    }
  ];
};

const countrySelectStyles = ({ theme }: { theme: Theme }) => ({
  control: (base: any) => ({
    ...base,
    minHeight: theme.spacing(5.375)
  }),
  option: (base: any) => ({
    ...base,
    color: theme.palette.text.primary
  }),
  singleValue: (base: any) => ({
    ...base,
    color: theme.palette.text.primary
  }),
  indicatorSeparator: (base: any) => ({
    ...base,
    display: 'none'
  })
});

interface GoogleDomainsDropdownProps {
  disabled: boolean;
  items: { [key: string]: SerpCountry };
  onChange: (value: ValueType<SerpSearchEngine, false>) => void;
  value?: SerpSearchEngine;
}

function GoogleDomainsDropdown ({ disabled, items, onChange, value }: GoogleDomainsDropdownProps) {
  const theme = useTheme();
  const classes = useStyles();
  const { translate } = useI18n();

  const { searchEngines, otherSearchEngines } = mapToCountryLanguageList(items).reduce(
    ({ searchEngines, otherSearchEngines }: { searchEngines: { [key: string]: SerpSearchEngineLabel }, otherSearchEngines: string[] }, searchEngineCode: string) => {
      searchEngines[searchEngineCode] = enrichCountryLanguage(searchEngineCode);
      if (topSearchEngines.indexOf(searchEngineCode) === -1) {
        otherSearchEngines.push(searchEngineCode);
      }
      return { searchEngines, otherSearchEngines };
    },
    { searchEngines: {}, otherSearchEngines: [] }
  );

  const groupedCountries = getGroupedCountries({
    searchEngines,
    otherSearchEngines,
    translate
  });

  const handleChange = async (searchEngine: ValueType<SerpSearchEngine, false>) => {
    await onChange(searchEngine);
  };

  const isOptionSelected = ({ country, language }: { country: string, language: string }) => {
    return country === value?.country && language === value?.language;
  };

  return (
    <Select
      value={value as SerpSearchEngine}
      onChange={handleChange}
      options={groupedCountries}
      isOptionSelected={isOptionSelected}
      styles={countrySelectStyles({ theme })}
      isDisabled={disabled}
      components={{
        DropdownIndicator ({ data, ...props }: any) {
          return (
            <components.DropdownIndicator {...props}>
              <Icon type='triangle-down' size='sm' color='grey' />
            </components.DropdownIndicator>
          );
        },
        Option ({ data, ...props }: any) {
          return (
            <components.Option {...props}>
              <span className={classes.flag}>
                <Flag country={tldToCountryCode(data.country)} />
              </span>
              <span className={classes.dropdownOptionsText}>
                {data.label}
              </span>
            </components.Option>
          );
        },
        SingleValue ({ data, ...props }: any) {
          return (
            <components.SingleValue {...props}>
              <span className={classes.flag}>
                <Flag country={tldToCountryCode(data.country)} />
              </span>
              <span className={classes.selectedOptionText}>
                {getLabel(searchEngines[`${data.country}_${data.language}`], translate).label}
              </span>
            </components.SingleValue>
          );
        }
      }}
    />
  );
}

export default GoogleDomainsDropdown;
